import { Box, Card, CardContent, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CheckboxControl from '../Checkboxes/CheckboxControl'
import { useTranslation } from 'react-i18next'
import AddressForm from '../Address/AddressForm'
import { useCountryContext } from '../Context/CountryContext'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { Address, InvoiceSignature } from '../../swagger'
import { defaultAddress } from '../Account/invoiceProfile/InvoiceProfileLayout'
import { invoicesApi } from '../../api/swagger'
import { FieldValidity } from '../Interfaces/FieldValidity'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { validateEmailText } from '../../helpers/validateEmail'
import useEmailMessage from '../../hooks/useEmailMessage'
import useEndpoint from '../../hooks/useEndpoint'
import { LoadingContext } from '../Context/LoadingContext'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../Context/SnackbarContext'
import CardFormHeader from '../Card/CardFormHeader'
import ImageUploader from '../Images/ImageUploader'

export interface InvoiceSignatureFormProps {
  invoiceSignatureDetails: InvoiceSignature
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleImageChange: (base64EncodedData: string | null) => void
  originUseDefaultSignature: boolean
  refresh: () => void
}

export interface InvoiceSignatureFormValidFields {
  payableTo: FieldValidity
  email: FieldValidity
  closingMessage: FieldValidity
}

export const InvoiceSignatureForm: React.FC<InvoiceSignatureFormProps> = ({
  invoiceSignatureDetails,
  handleInputChange,
  handleImageChange,
  originUseDefaultSignature,
  refresh,
}) => {
  const {
    Invoice: { updateInvoiceSignature: updateInvoiceSignatureLoadingId },
  } = useLoadingIds()
  const { countryOptions } = useCountryContext()
  const isShowOnDesktop = useShowOnDesktop()
  const { setSnackbarState, setSnackbarMessage, setSnackbarSeverity } =
    useSnackbarContext()
  const { validationMessages } = useEmailMessage()
  const { t } = useTranslation()
  const [isEditVariant, setIsEditVariant] = useState(false)
  const [
    invoiceSignatureFieldValidity,
    setInvoiceSignatureFieldValidityFieldValidity,
  ] = useState<InvoiceSignatureFormValidFields>({
    payableTo: { input: true },
    email: { input: true },
    closingMessage: { input: true, length: true },
  })
  const [textErrorEmail, setTextErrorEmail] = useState<string>('')
  const { loadingIds } = React.useContext(LoadingContext)
  const {
    address = defaultAddress,
    payableTo,
    closingMessage,
    email,
    phoneNumber,
    useDefaultSignature,
    qrCodeImage,
  } = invoiceSignatureDetails

  const hasUpdateLoadingId = loadingIds.has(updateInvoiceSignatureLoadingId)
  const title = t('InvoiceSignatureForm.Title', 'Invoice Signature')
  const subtitle = t(
    'InvoiceSignatureForm.SubTitle',
    'Signature will be applied across all invoices'
  )
  const requiredMessage = t(
    'InvoiceSignatureForm.Required.Information',
    ' Indicates a required field'
  )

  const handleChecked = (value: boolean) => {
    const e = {
      target: { name: 'useDefaultSignature', value },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    handleInputChange(e)
  }
  const checkFieldValidity = () => {
    const resultMsg = email?.trim() ? validateEmailText(email) : ''
    const isMaxLengthClosingMessage = (closingMessage ?? '').length <= 90
    setTextErrorEmail(() => validationMessages(resultMsg))
    setInvoiceSignatureFieldValidityFieldValidity((prev) => ({
      ...prev,
      payableTo: { input: !!payableTo },
      email: { input: resultMsg === '' },
      closingMessage: { input: isMaxLengthClosingMessage },
    }))
    return !!payableTo && !resultMsg && isMaxLengthClosingMessage
  }

  const isEditedAddressEqualToDefault = () => {
    const editedAddressProps = Object.keys(
      invoiceSignatureDetails?.address ?? {}
    )

    return editedAddressProps.every(
      (key) =>
        invoiceSignatureDetails?.address?.[key as keyof Address] ===
        defaultAddress[key as keyof Address]
    )
  }

  const { triggerFetch: updateInvoiceSignature } = useEndpoint({
    loadingId: updateInvoiceSignatureLoadingId,
    async swaggerCall() {
      if (
        isEditedAddressEqualToDefault() &&
        invoiceSignatureDetails.addressKey
      ) {
        await invoicesApi.deleteInvoiceSignatureAddress({
          addressKey: invoiceSignatureDetails.addressKey,
        })

        delete invoiceSignatureDetails.address
      }
      await invoicesApi.updateInvoiceSignature({
        body: { ...invoiceSignatureDetails },
      })
    },
    failureCallback: async (_, done) => {
      done()
      setSnackbarMessage(
        t(
          'InvoiceSignatureForm.UpdateInvoiceSignature.ValidationMessage.Error',
          'Something went wrong while updating the invoice signature'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarState(true)
    },
    successCallback: () => {
      setIsEditVariant(false)
      refresh()
    },
  })

  const handleSave = () => {
    if (!checkFieldValidity()) {
      return
    }
    updateInvoiceSignature()
  }

  const handleClearAddress = () => {
    for (const prop in defaultAddress) {
      const event = {
        target: {
          name: prop,
          value: defaultAddress[prop as keyof Address],
        },
      } as React.ChangeEvent<HTMLInputElement>

      handleInputChange(event)
    }
  }

  const showOnlyUseDefaultSignature =
    originUseDefaultSignature && useDefaultSignature

  useEffect(() => {
    setIsEditVariant(showOnlyUseDefaultSignature || isEditVariant)
  }, [isEditVariant, showOnlyUseDefaultSignature])

  return (
    <>
      {!showOnlyUseDefaultSignature && (
        <Typography mx={3} style={{ textAlign: 'end' }}>
          <Typography component={'span'} color={'red'}>
            *
          </Typography>
          {requiredMessage}
        </Typography>
      )}
      <Card>
        <CardFormHeader
          header={
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'max-content auto',
                  md: 'max-content auto',
                },
                gap: 1,
                alignItems: 'end',
              }}
            >
              <Typography variant="h5">{title}</Typography>

              <Typography>
                <strong>({subtitle})</strong>
              </Typography>
            </Box>
          }
        />
        <CardContent>
          {/* Checkbox */}
          <CheckboxControl
            disabled={!isEditVariant}
            label={t(
              'InvoiceSignature.Field.useDefaultSignature',
              'Use default signature'
            )}
            name="useDefaultSignature"
            checkboxValue={useDefaultSignature}
            OnChange={handleChecked}
          />

          {!showOnlyUseDefaultSignature && (
            <Box
              mt={2}
              pb={3}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  sm: '1fr',
                  md: '1fr 1fr',
                },
              }}
              gap={3}
            >
              <TextField
                id="PayableToField"
                label={
                  <>
                    {t('InvoiceSignature.Field.PayableTo', 'Make Payable To: ')}
                    <Typography component={'span'} color={'red'}>
                      *
                    </Typography>
                  </>
                }
                name="payableTo"
                variant="filled"
                disabled={!isEditVariant}
                value={payableTo}
                onChange={handleInputChange}
                error={!invoiceSignatureFieldValidity.payableTo.input}
                helperText={
                  !invoiceSignatureFieldValidity.payableTo.input
                    ? t(
                        'InvoiceSignatureForm.ValidationMessage.payableTo',
                        'Make Payable To cannot be empty'
                      )
                    : null
                }
              />
              <ContainedButton
                disabled={!isEditVariant}
                variant={ContainedButtonVariant.ClearAddress}
                id="clear-address-button"
                css={{
                  height: '32px',
                  width: isShowOnDesktop ? 'auto' : '100%',
                  justifySelf: 'end',
                }}
                onClick={handleClearAddress}
              />
            </Box>
          )}

          {/* Address fields */}
          {!showOnlyUseDefaultSignature && (
            <>
              <AddressForm
                fullWidthFields
                addressInformation={address}
                isFieldDisabled={!isEditVariant}
                countryOptions={countryOptions}
                onInputChange={handleInputChange}
                gridColumnPaddingBottom={0}
              />
              <Box
                pb={3}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    sm: '1fr',
                    md: '1fr 1fr',
                  },
                  alignItems: 'center',
                }}
                gap={3}
              >
                <TextField
                  id="PhoneNumberField"
                  label={t(
                    'InvoiceSignature.Field.PhoneNumber',
                    'Phone Number'
                  )}
                  variant="filled"
                  name="phoneNumber"
                  disabled={!isEditVariant}
                  value={phoneNumber ?? ''}
                  onChange={handleInputChange}
                />
                <TextField
                  id="EmailField"
                  label={t('InvoiceSignature.Field.Email', 'Email')}
                  variant="filled"
                  name="email"
                  disabled={!isEditVariant}
                  value={email ?? ''}
                  onChange={handleInputChange}
                  error={!invoiceSignatureFieldValidity.email.input}
                  helperText={
                    !invoiceSignatureFieldValidity.email.input
                      ? textErrorEmail
                      : null
                  }
                />

                <TextField
                  id="ClosingMessageField"
                  label={t(
                    'InvoiceSignature.Field.ClosingMessage',
                    'Closing Message (90 characters maximum)'
                  )}
                  variant="filled"
                  name={'closingMessage'}
                  disabled={!isEditVariant}
                  value={closingMessage ?? ''}
                  onChange={handleInputChange}
                  error={!invoiceSignatureFieldValidity.closingMessage.input}
                  helperText={
                    !invoiceSignatureFieldValidity.closingMessage.input
                      ? t(
                          'InvoiceSignature.InvoiceSignature.HelperText.MaxCharacters',
                          '90 characters max'
                        )
                      : null
                  }
                />
              </Box>
              <Box
                pb={3}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    sm: '1fr',
                    md: '1fr 1fr',
                  },
                  alignItems: 'right',
                }}
                gap={3}
              >
                <ImageUploader
                  title={t(
                    'InvoiceSignatureForm.QRCodeUploadForm.Title',
                    'QR Code For Accepting Payments'
                  )}
                  isEditVariant={isEditVariant}
                  initialImageBase64={qrCodeImage}
                  onImageUpdate={handleImageChange}
                />
                <ContainedButton
                  disabled={hasUpdateLoadingId}
                  variant={
                    isEditVariant
                      ? ContainedButtonVariant.Save
                      : ContainedButtonVariant.Edit
                  }
                  id="edit-save-button"
                  css={{
                    height: '32px',
                    width: isShowOnDesktop ? '76px' : '100%',
                    justifySelf: 'end',
                  }}
                  onClick={
                    isEditVariant && !hasUpdateLoadingId
                      ? () => handleSave()
                      : () => setIsEditVariant(true)
                  }
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </>
  )
}
